import { useState, useMemo, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useExamSections, useExamStats, useLeaderboard, useQuestionsFeed, useGeneralExams, useActiveSessions, useCreateSession, useDeleteSession, useCompleteSession, useSessionHistory, useFriends } from '../lib/api';
import type { ExamSectionStat, StudySession } from '../lib/api';
import { deriveCategories, buildCategoryIndex } from '../lib/topic-tags';
import { VerifiedBadge } from '../components/VerifiedBadge';
import { getFlaggedQuestions } from '../lib/flagged-questions';

export function ExamDashboardPage() {
  const { examId } = useParams();
  const navigate = useNavigate();
  const { data: examsData } = useGeneralExams();
  const { data: sectionsData } = useExamSections(examId ?? null);
  const { data: statsData } = useExamStats(examId ?? null);
  const { data: lb } = useLeaderboard('exam', examId ?? null);
  const { data: sessionsData } = useActiveSessions({ examId: examId ?? null });
  const createSession = useCreateSession();
  const deleteSession = useDeleteSession();
  const completeSession = useCompleteSession();
  const { data: historyData } = useSessionHistory({ examId: examId ?? null });

  const exam = examsData?.exams?.find(e => e.id === examId);
  const sections = sectionsData?.sections ?? [];
  const stats = statsData?.stats;
  const activeSessions = sessionsData?.sessions ?? [];

  const scienceSections = sections.filter(s => s.code !== 'cars');
  const verbalSections = sections.filter(s => s.code === 'cars');
  const hasScienceVerbalSplit = verbalSections.length > 0 && scienceSections.length > 0;

  // Topic filter state
  const [showTopicPicker, setShowTopicPicker] = useState(false);
  const [studyTarget, setStudyTarget] = useState<'science' | 'verbal' | 'section'>('science');
  const [targetSectionId, setTargetSectionId] = useState<string | null>(null);
  const [selectedTopics, setSelectedTopics] = useState<Set<string>>(new Set());
  const [questionFilter, setQuestionFilter] = useState<'all' | 'mine' | 'friends'>('all');
  const [studyMode, setStudyMode] = useState<'all' | 'passages' | 'discrete'>('all');
  const [sessionCount, setSessionCount] = useState(20);
  const [sessionName, setSessionName] = useState('');
  const [unansweredOnly, setUnansweredOnly] = useState(false);
  const [answeredWrongOnly, setAnsweredWrongOnly] = useState(false);
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);

  const feedSectionId = targetSectionId ?? (studyTarget === 'verbal' ? verbalSections[0]?.id : null);
  const feedParams = feedSectionId
    ? { exam_section_id: feedSectionId }
    : (studyTarget === 'science' && examId ? { exam_id: examId } : {});
  const { data: feedData, isLoading: feedLoading } = useQuestionsFeed(feedParams);

  const allStudyableQuestions = useMemo(
    () => (feedData?.questions ?? []).filter((q: any) => q.type !== 'flashcard' && q.type !== 'quick_mcq'),
    [feedData?.questions],
  );
  const { data: friendsData } = useFriends();
  const friendIds = useMemo(
    () => new Set((friendsData?.friends ?? []).filter(f => f.status === 'accepted').map(f => f.id)),
    [friendsData],
  );
  const studyableQuestions = useMemo(() => {
    let qs = allStudyableQuestions;
    if (questionFilter === 'mine' && feedData?.currentUserId) {
      qs = qs.filter((q: any) => q.authorUserId === feedData.currentUserId);
    } else if (questionFilter === 'friends') {
      qs = qs.filter((q: any) => q.authorUserId && friendIds.has(q.authorUserId));
    }
    if (studyMode === 'passages') {
      qs = qs.filter((q: any) => q.payload?.passage_type === 'passage-based' || q.payload?.passage);
    } else if (studyMode === 'discrete') {
      qs = qs.filter((q: any) => q.payload?.passage_type !== 'passage-based' && !q.payload?.passage);
    }
    return qs;
  }, [allStudyableQuestions, questionFilter, feedData?.currentUserId, friendIds, studyMode]);
  const categoryIndex = useMemo(() => buildCategoryIndex(studyableQuestions), [studyableQuestions]);
  const availableTopics = useMemo(() => {
    return Array.from(categoryIndex.entries())
      .filter(([name]) => !/^mcat$/i.test(name))
      .sort((a, b) => b[1].size - a[1].size)
      .map(([name, qIds]) => ({ name, count: qIds.size }));
  }, [categoryIndex]);

  const allSelected = selectedTopics.size === 0;
  const examDefaultLabel = useMemo(() => {
    const topicNames = allSelected ? [] : Array.from(selectedTopics);
    return allSelected
      ? 'All Topics'
      : topicNames.slice(0, 3).join(', ') + (topicNames.length > 3 ? ` +${topicNames.length - 3}` : '');
  }, [allSelected, selectedTopics]);
  const answeredSet = useMemo(
    () => new Set(feedData?.answeredQuestionIds ?? []),
    [feedData?.answeredQuestionIds],
  );
  const wrongSet = useMemo(
    () => new Set(feedData?.wrongQuestionIds ?? []),
    [feedData?.wrongQuestionIds],
  );

  const applyFilters = (pool: typeof studyableQuestions) => {
    if (unansweredOnly) pool = pool.filter(q => !answeredSet.has(q.id));
    if (answeredWrongOnly) pool = pool.filter(q => wrongSet.has(q.id));
    return pool;
  };

  const selectedQuestionCount = useMemo(() => {
    let pool = studyableQuestions;
    if (!allSelected) {
      const ids = new Set<string>();
      for (const cat of selectedTopics) {
        const qIds = categoryIndex.get(cat);
        if (qIds) qIds.forEach(id => ids.add(id));
      }
      pool = pool.filter(q => ids.has(q.id));
    }
    pool = applyFilters(pool);
    return pool.length;
  }, [allSelected, selectedTopics, categoryIndex, studyableQuestions, unansweredOnly, answeredWrongOnly, answeredSet, wrongSet]);

  const filteredTopicCounts = useMemo(() => {
    const counts = new Map<string, number>();
    for (const [name, qIds] of categoryIndex.entries()) {
      const topicQuestions = studyableQuestions.filter(q => qIds.has(q.id));
      counts.set(name, applyFilters(topicQuestions).length);
    }
    return counts;
  }, [categoryIndex, studyableQuestions, unansweredOnly, answeredWrongOnly, answeredSet, wrongSet]);

  const getFilteredQuestionIds = (): string[] => {
    let qs = studyableQuestions;
    if (!allSelected) {
      const ids = new Set<string>();
      for (const cat of selectedTopics) {
        const qIds = categoryIndex.get(cat);
        if (qIds) qIds.forEach(id => ids.add(id));
      }
      qs = qs.filter(q => ids.has(q.id));
    }
    qs = applyFilters(qs);
    if (studyMode === 'passages') {
      const grouped = new Map<string, typeof qs>();
      const noPassage: typeof qs = [];
      for (const q of qs) {
        const passageKey = (q.payload as any)?.passage?.slice(0, 100);
        if (passageKey) {
          const group = grouped.get(passageKey) ?? [];
          group.push(q);
          grouped.set(passageKey, group);
        } else {
          noPassage.push(q);
        }
      }
      return [...Array.from(grouped.values()).flat(), ...noPassage].map(q => q.id);
    }
    return qs.map(q => q.id);
  };

  const createAndNavigate = async (sectionId: string) => {
    let questionIds = getFilteredQuestionIds();
    if (questionIds.length === 0) return;

    // Cap to the user's chosen count. Passage mode keeps whole passage groups
    // intact (the ids are already grouped), so we don't slice there.
    if (studyMode !== 'passages') {
      for (let i = questionIds.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [questionIds[i], questionIds[j]] = [questionIds[j]!, questionIds[i]!];
      }
      questionIds = questionIds.slice(0, Math.min(Math.max(1, sessionCount), questionIds.length));
    }

    const topicNames = allSelected ? [] : Array.from(selectedTopics);
    const base = sessionName.trim() || examDefaultLabel;
    const taken = new Set(activeSessions.map(s => s.label).filter(Boolean));
    let label = base;
    if (taken.has(label)) { let n = 2; while (taken.has(`${base} (${n})`)) n++; label = `${base} (${n})`; }

    const result = await createSession.mutateAsync({
      examSectionId: sectionId,
      filters: allSelected ? null : { topics: topicNames },
      questionIds,
      label,
    });

    navigate(`/study/exam/${sectionId}?session=${result.session.id}`);
  };

  // Don't open the topic picker until the question feed has loaded, otherwise it
  // opens with no topics/questions (and confirm is disabled) on a freshly-opened
  // section until the user backs out and retries.
  const handleStartScience = () => {
    if (feedLoading) return;
    setStudyTarget('science');
    setTargetSectionId(null);
    setSelectedTopics(new Set());
    setSessionName('');
    setShowTopicPicker(true);
  };

  const handleStartVerbal = () => {
    if (feedLoading) return;
    setStudyTarget('verbal');
    setTargetSectionId(verbalSections.length === 1 ? verbalSections[0]!.id : null);
    setSelectedTopics(new Set());
    setSessionName('');
    setShowTopicPicker(true);
  };

  const handleStartSection = (sectionId: string) => {
    if (feedLoading) return;
    setStudyTarget('section');
    setTargetSectionId(sectionId);
    setSelectedTopics(new Set());
    setSessionName('');
    setShowTopicPicker(true);
  };

  const confirmTopics = () => {
    setShowTopicPicker(false);
    const sid = targetSectionId
      ?? (studyTarget === 'verbal' ? verbalSections[0]?.id : scienceSections[0]?.id);
    if (sid) createAndNavigate(sid);
  };

  const toggleTopic = (topic: string) => {
    setSelectedTopics(prev => {
      const next = new Set(prev);
      if (next.has(topic)) next.delete(topic);
      else next.add(topic);
      return next;
    });
  };

  const resumeSession = (session: StudySession) => {
    const sectionId = session.examSectionId ?? session.classSectionId;
    if (!sectionId) return;
    const contextType = session.examSectionId ? 'exam' : 'class';
    navigate(`/study/${contextType}/${sectionId}?session=${session.id}`);
  };

  if (!exam) {
    return <div className="flex items-center justify-center min-h-[60vh] text-slate-500">Loading...</div>;
  }

  const previewSessions = activeSessions.slice(0, 2);
  const previewSections = stats?.sections.slice(0, 2) ?? [];
  const previewLeaderboard = lb?.entries.slice(0, 3) ?? [];

  return (
    <div className="max-w-lg mx-auto px-4 pb-20 space-y-5">
      {/* Header + inline stats */}
      <div className="flex items-center gap-3 pt-1">
        <Link to="/exams" className="text-squishy-primary shrink-0">
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-5 h-5">
            <path fillRule="evenodd" d="M17 10a.75.75 0 01-.75.75H5.612l4.158 3.96a.75.75 0 11-1.04 1.08l-5.5-5.25a.75.75 0 010-1.08l5.5-5.25a.75.75 0 111.04 1.08L5.612 9.25H16.25A.75.75 0 0117 10z" clipRule="evenodd" />
          </svg>
        </Link>
        <h1 className="text-lg font-bold text-squishy-dark">{exam.code}</h1>
        <div className="flex items-center gap-2 ml-auto text-[11px] text-slate-500">
          <span className="font-semibold text-squishy-primary">{stats?.totals.attempted ?? 0}<span className="text-slate-400 font-normal">/{stats?.totals.total ?? 0}</span></span>
          <span className="text-slate-300">|</span>
          <span className={`font-semibold ${(stats?.totals.accuracy ?? 0) >= 70 ? 'text-emerald-600' : (stats?.totals.accuracy ?? 0) >= 50 ? 'text-amber-500' : 'text-slate-500'}`}>{stats?.totals.accuracy ?? 0}%</span>
        </div>
      </div>

      {/* Start Studying */}
      {hasScienceVerbalSplit ? (
        <div className="grid grid-cols-2 gap-2">
          <button
            onClick={handleStartScience}
            disabled={feedLoading}
            className="bg-gradient-to-br from-teal-500 to-emerald-600 text-white py-3 rounded-xl font-bold text-sm active:scale-[0.97] transition-transform disabled:opacity-60"
          >
            {feedLoading ? 'Loading…' : 'Start Science'}
          </button>
          <button
            onClick={handleStartVerbal}
            disabled={feedLoading}
            className="bg-gradient-to-br from-indigo-500 to-purple-600 text-white py-3 rounded-xl font-bold text-sm active:scale-[0.97] transition-transform disabled:opacity-60"
          >
            {feedLoading ? 'Loading…' : 'Start Verbal'}
          </button>
        </div>
      ) : (
        <button
          onClick={handleStartScience}
          disabled={feedLoading}
          className="w-full bg-gradient-to-br from-squishy-primary to-teal-600 text-white py-3 rounded-xl font-bold text-sm active:scale-[0.97] transition-transform disabled:opacity-60"
        >
          {feedLoading ? 'Loading…' : 'Start Studying'}
        </button>
      )}

      {/* 3. Continue Studying (2 most recent + View All) */}
      {activeSessions.length > 0 && (
        <div className="space-y-2">
          <h2 className="text-sm font-semibold text-slate-600 uppercase tracking-wide">Continue Studying</h2>
          {previewSessions.map(session => (
            <SessionCard
              key={session.id}
              session={session}
              sections={sections}
              onResume={() => resumeSession(session)}
              onDelete={() => setConfirmDeleteId(session.id)}
            />
          ))}
          {activeSessions.length > 2 && (
            <Link
              to={`/exam/${examId}/sessions`}
              className="flex items-center justify-center gap-2 w-full py-3 bg-slate-100 hover:bg-slate-200 rounded-xl text-sm font-semibold text-squishy-primary transition-colors"
            >
              View All Sessions ({activeSessions.length})
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-4 h-4">
                <path fillRule="evenodd" d="M7.21 14.77a.75.75 0 01.02-1.06L11.168 10 7.23 6.29a.75.75 0 111.04-1.08l4.5 4.25a.75.75 0 010 1.08l-4.5 4.25a.75.75 0 01-1.06-.02z" clipRule="evenodd" />
              </svg>
            </Link>
          )}
        </div>
      )}

      {/* 4. Marked for Review */}
      <MarkedForReviewCard
        allQuestions={allStudyableQuestions}
        createSession={createSession}
        navigate={navigate}
        sections={sections}
        scienceSections={scienceSections}
        contextType="exam"
      />

      {/* 5. By Topic (first 2 + View All) */}
      {stats && stats.sections.length > 0 && (
        <div className="space-y-2">
          <h2 className="text-sm font-semibold text-slate-600 uppercase tracking-wide">By Topic</h2>
          {previewSections.map(s => (
            <SectionStatRow key={s.sectionId} stat={s} onStart={() => handleStartSection(s.sectionId)} />
          ))}
          {stats.sections.length > 2 && (
            <Link
              to={`/exam/${examId}/topics`}
              className="flex items-center justify-center gap-2 w-full py-3 bg-slate-100 hover:bg-slate-200 rounded-xl text-sm font-semibold text-squishy-primary transition-colors"
            >
              View All Topics ({stats.sections.length})
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-4 h-4">
                <path fillRule="evenodd" d="M7.21 14.77a.75.75 0 01.02-1.06L11.168 10 7.23 6.29a.75.75 0 111.04-1.08l4.5 4.25a.75.75 0 010 1.08l-4.5 4.25a.75.75 0 01-1.06-.02z" clipRule="evenodd" />
              </svg>
            </Link>
          )}
        </div>
      )}

      {/* 6. Leaderboard (top 3 + View All) */}
      <div className="bg-white border rounded-xl overflow-hidden">
        <div className="flex items-center justify-between px-4 pt-3 pb-2">
          <h2 className="font-semibold text-squishy-dark text-sm">Leaderboard</h2>
          <span className="text-[10px] text-slate-400 uppercase tracking-wide">30 day</span>
        </div>
        <ol className="divide-y">
          {previewLeaderboard.map(e => (
            <li key={e.userId} className={`flex items-center gap-2.5 px-4 py-2.5 ${e.isCurrentUser ? 'bg-teal-50' : ''}`}>
              <span className="w-5 text-right text-xs font-mono text-slate-400">{e.rank}</span>
              <div className="w-7 h-7 rounded-full bg-slate-200 overflow-hidden flex items-center justify-center text-[10px] font-medium shrink-0">
                {e.avatarUrl ? <img src={e.avatarUrl} alt="" className="w-full h-full object-cover" /> : e.displayName[0]}
              </div>
              <span className="flex-1 text-sm font-medium truncate inline-flex items-center gap-1">
                <span className="truncate">{e.displayName}</span>
                {e.verifiedStudent && <VerifiedBadge />}
              </span>
              <span className="text-xs font-mono text-squishy-dark">{e.xp.toLocaleString()} XP</span>
            </li>
          ))}
          {(!lb?.entries || lb.entries.length === 0) && (
            <li className="px-4 py-6 text-center text-slate-400 text-sm">No activity yet — be the first!</li>
          )}
        </ol>
        {lb?.entries && lb.entries.length > 3 && (
          <Link
            to={`/exam/${examId}/leaderboard`}
            className="flex items-center justify-center gap-2 w-full py-3 border-t bg-slate-50 hover:bg-slate-100 text-sm font-semibold text-squishy-primary transition-colors"
          >
            View Full Leaderboard
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-4 h-4">
              <path fillRule="evenodd" d="M7.21 14.77a.75.75 0 01.02-1.06L11.168 10 7.23 6.29a.75.75 0 111.04-1.08l4.5 4.25a.75.75 0 010 1.08l-4.5 4.25a.75.75 0 01-1.06-.02z" clipRule="evenodd" />
            </svg>
          </Link>
        )}
      </div>

      {/* ── Delete Session Confirmation ─────────────────────────── */}
      {confirmDeleteId && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center px-6 animate-fade-in">
          <div className="bg-white rounded-2xl p-5 w-full max-w-xs shadow-xl text-center space-y-4 animate-slide-up">
            <div className="text-3xl">&#x1F5D1;</div>
            <p className="text-sm font-semibold text-squishy-navy">Discard this session?</p>
            <p className="text-xs text-slate-500">Your progress will be lost and can't be recovered.</p>
            <div className="flex gap-2">
              <button
                onClick={() => setConfirmDeleteId(null)}
                className="flex-1 py-2.5 rounded-xl border-2 border-slate-200 text-sm font-semibold text-slate-600 hover:bg-slate-50 transition active:scale-[0.97]"
              >
                Keep
              </button>
              <button
                onClick={() => { deleteSession.mutate(confirmDeleteId); setConfirmDeleteId(null); }}
                className="flex-1 py-2.5 rounded-xl bg-red-500 text-white text-sm font-semibold hover:bg-red-600 transition active:scale-[0.97]"
              >
                Discard
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Topic Picker Modal */}
      {showTopicPicker && (
        <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center">
          <div className="absolute inset-0 bg-black/40" onClick={() => setShowTopicPicker(false)} />
          <div className="relative bg-white w-full max-w-md rounded-t-2xl sm:rounded-2xl max-h-[80vh] flex flex-col animate-slide-up">
            <div className="px-5 pt-5 pb-3 border-b">
              <div className="flex items-center justify-between">
                <h2 className="text-lg font-bold text-squishy-dark">Choose Topics</h2>
                <button onClick={() => setShowTopicPicker(false)} className="text-slate-400 hover:text-slate-600 p-1">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              <p className="text-xs text-slate-500 mt-1">Select topics to focus on, or keep All selected.</p>
              <div className="mt-3 flex bg-slate-100 rounded-lg p-0.5 gap-0.5">
                <button
                  onClick={() => { setQuestionFilter('all'); setSelectedTopics(new Set()); }}
                  className={`flex-1 py-1.5 text-[11px] font-semibold rounded-md transition-all ${
                    questionFilter === 'all' ? 'bg-white text-squishy-primary shadow-sm' : 'text-slate-500'
                  }`}
                >
                  All ({allStudyableQuestions.length})
                </button>
                <button
                  onClick={() => { setQuestionFilter('mine'); setSelectedTopics(new Set()); }}
                  className={`flex-1 py-1.5 text-[11px] font-semibold rounded-md transition-all ${
                    questionFilter === 'mine' ? 'bg-white text-squishy-primary shadow-sm' : 'text-slate-500'
                  }`}
                >
                  My Q ({allStudyableQuestions.filter((q: any) => q.authorUserId === feedData?.currentUserId).length})
                </button>
                <button
                  onClick={() => { setQuestionFilter('friends'); setSelectedTopics(new Set()); }}
                  className={`flex-1 py-1.5 text-[11px] font-semibold rounded-md transition-all ${
                    questionFilter === 'friends' ? 'bg-white text-squishy-primary shadow-sm' : 'text-slate-500'
                  }`}
                >
                  Friends Q ({allStudyableQuestions.filter((q: any) => q.authorUserId && friendIds.has(q.authorUserId)).length})
                </button>
              </div>
            </div>

            {(() => {
              const passageCount = allStudyableQuestions.filter((q: any) => q.payload?.passage_type === 'passage-based' || q.payload?.passage).length;
              const discreteCount = allStudyableQuestions.length - passageCount;
              if (passageCount === 0) return null;
              return (
                <div className="px-5 py-2 border-b">
                  <p className="text-[10px] text-slate-400 font-semibold uppercase tracking-wide mb-1.5">Study Mode</p>
                  <div className="flex bg-slate-100 rounded-lg p-0.5">
                    <button
                      onClick={() => { setStudyMode('all'); setSelectedTopics(new Set()); }}
                      className={`flex-1 py-1.5 text-xs font-semibold rounded-md transition-all ${
                        studyMode === 'all' ? 'bg-white text-squishy-primary shadow-sm' : 'text-slate-500'
                      }`}
                    >All</button>
                    <button
                      onClick={() => { setStudyMode('passages'); setSelectedTopics(new Set()); }}
                      className={`flex-1 py-1.5 text-xs font-semibold rounded-md transition-all ${
                        studyMode === 'passages' ? 'bg-white text-indigo-600 shadow-sm' : 'text-slate-500'
                      }`}
                    >Passages ({passageCount})</button>
                    <button
                      onClick={() => { setStudyMode('discrete'); setSelectedTopics(new Set()); }}
                      className={`flex-1 py-1.5 text-xs font-semibold rounded-md transition-all ${
                        studyMode === 'discrete' ? 'bg-white text-squishy-primary shadow-sm' : 'text-slate-500'
                      }`}
                    >Discrete ({discreteCount})</button>
                  </div>
                </div>
              );
            })()}

            {(targetSectionId || studyTarget === 'verbal' || studyTarget === 'science' || scienceSections.length === 1) && (
              <>
                {/* Unanswered Only toggle */}
                <div className="px-5 pt-3 pb-1 space-y-2">
                  <label className="flex items-center justify-between cursor-pointer">
                    <div>
                      <span className="text-sm font-medium text-squishy-dark">Unanswered only</span>
                      <span className="ml-2 text-xs text-slate-400">
                        ({studyableQuestions.filter(q => !answeredSet.has(q.id)).length} new)
                      </span>
                    </div>
                    <div
                      onClick={() => { setUnansweredOnly(v => !v); setAnsweredWrongOnly(false); }}
                      className={`relative rounded-full transition-colors ${unansweredOnly ? 'bg-squishy-primary' : 'bg-slate-300'}`}
                      style={{ width: 40, height: 22 }}
                    >
                      <div
                        className="absolute top-0.5 left-0.5 bg-white rounded-full shadow transition-transform"
                        style={{ width: 18, height: 18, transform: unansweredOnly ? 'translateX(18px)' : 'translateX(0)' }}
                      />
                    </div>
                  </label>
                  {/* Answered Wrong Only toggle */}
                  <label className="flex items-center justify-between cursor-pointer">
                    <div>
                      <span className="text-sm font-medium text-squishy-dark">Answered wrong only</span>
                      <span className="ml-2 text-xs text-slate-400">
                        ({wrongSet.size} to retry)
                      </span>
                    </div>
                    <div
                      onClick={() => { setAnsweredWrongOnly(v => !v); setUnansweredOnly(false); }}
                      className={`relative rounded-full transition-colors ${answeredWrongOnly ? 'bg-red-500' : 'bg-slate-300'}`}
                      style={{ width: 40, height: 22 }}
                    >
                      <div
                        className="absolute top-0.5 left-0.5 bg-white rounded-full shadow transition-transform"
                        style={{ width: 18, height: 18, transform: answeredWrongOnly ? 'translateX(18px)' : 'translateX(0)' }}
                      />
                    </div>
                  </label>
                </div>
                <div className="flex-1 overflow-y-auto px-5 py-3 space-y-2">
                  <button
                    onClick={() => setSelectedTopics(new Set())}
                    className={`w-full flex items-center justify-between px-4 py-3 rounded-xl border-2 transition-all active:scale-[0.98] ${
                      allSelected ? 'border-squishy-primary bg-teal-50 text-squishy-primary' : 'border-slate-200 text-slate-600'
                    }`}
                  >
                    <span className="font-medium">All Topics</span>
                    <span className="text-xs text-slate-400">{applyFilters(studyableQuestions).length} questions</span>
                  </button>
                  {availableTopics.map(t => {
                    const filteredCount = filteredTopicCounts.get(t.name) ?? t.count;
                    return (
                      <button
                        key={t.name}
                        onClick={() => toggleTopic(t.name)}
                        className={`w-full flex items-center justify-between px-4 py-3 rounded-xl border-2 transition-all active:scale-[0.98] ${
                          selectedTopics.has(t.name) ? 'border-squishy-primary bg-teal-50 text-squishy-primary' : 'border-slate-200 text-slate-600'
                        }`}
                      >
                        <span className="font-medium text-left">{t.name}</span>
                        <span className="text-xs text-slate-400 shrink-0 ml-2">{filteredCount}</span>
                      </button>
                    );
                  })}
                </div>
                <div className="px-5 py-4 border-t space-y-3">
                  <div>
                    <label className="text-[11px] font-semibold text-slate-500 uppercase tracking-wide">Name this session</label>
                    <input
                      type="text"
                      value={sessionName}
                      onChange={(e) => setSessionName(e.target.value.slice(0, 60))}
                      placeholder={examDefaultLabel || 'e.g. Bio passages'}
                      maxLength={60}
                      className="mt-1 w-full border border-slate-200 rounded-lg px-3 py-2 text-sm text-slate-700 focus:outline-none focus:ring-2 focus:ring-squishy-primary/30"
                    />
                  </div>
                  {/* How many questions this session (passage mode keeps full passages) */}
                  {studyMode !== 'passages' && (
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium text-squishy-dark">Questions this session</span>
                      <div className="flex items-center gap-2">
                        <input
                          type="number"
                          min={1}
                          max={selectedQuestionCount}
                          value={Math.min(sessionCount, selectedQuestionCount) || ''}
                          onChange={(e) => setSessionCount(Math.max(1, parseInt(e.target.value, 10) || 1))}
                          className="w-16 border border-slate-200 rounded-lg px-2 py-1 text-center text-sm text-slate-700 focus:outline-none focus:ring-2 focus:ring-squishy-primary/30"
                        />
                        <span className="text-xs text-slate-400">/ {selectedQuestionCount} max</span>
                        <button
                          onClick={() => setSessionCount(selectedQuestionCount)}
                          className="text-xs font-semibold text-squishy-primary hover:underline"
                        >
                          Max
                        </button>
                      </div>
                    </div>
                  )}
                  <button
                    onClick={confirmTopics}
                    disabled={createSession.isPending || selectedQuestionCount === 0}
                    className="w-full bg-squishy-primary text-white py-3.5 rounded-xl font-semibold text-base hover:opacity-90 transition active:scale-[0.98] disabled:opacity-50"
                  >
                    {createSession.isPending
                      ? 'Creating...'
                      : studyMode === 'passages'
                        ? `Start Studying${!allSelected ? ` \u2014 ${selectedQuestionCount} question${selectedQuestionCount !== 1 ? 's' : ''}` : ''}`
                        : `Start Studying \u2014 ${Math.min(Math.max(1, sessionCount), selectedQuestionCount)} question${Math.min(Math.max(1, sessionCount), selectedQuestionCount) !== 1 ? 's' : ''}`}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// ── Session Card ────────────────────────────────────────────────────────────

function SessionCard({ session, sections, onResume, onDelete }: {
  session: StudySession;
  sections: any[];
  onResume: () => void;
  onDelete: () => void;
}) {
  const progress = session.totalQuestions > 0
    ? Math.round((session.currentIndex / session.totalQuestions) * 100)
    : 0;
  const remaining = session.totalQuestions - session.currentIndex;

  return (
    <div className="bg-white border rounded-xl px-3 py-2.5 flex items-center gap-2.5 active:bg-slate-50 transition-colors">
      <div className="flex-1 min-w-0 cursor-pointer" onClick={onResume}>
        <div className="flex items-center gap-2">
          <span className="text-xs font-medium text-squishy-dark truncate">{session.label || 'All Topics'}</span>
          <span className="text-[10px] text-slate-400 shrink-0">{remaining} left</span>
        </div>
        <div className="flex items-center gap-2 mt-1">
          <div className="flex-1 h-1.5 bg-slate-100 rounded-full overflow-hidden">
            <div className="h-full bg-teal-400 rounded-full transition-all" style={{ width: `${progress}%` }} />
          </div>
          <span className="text-[10px] text-slate-400 shrink-0 w-7 text-right">{progress}%</span>
        </div>
      </div>
      <button onClick={onResume} className="bg-squishy-primary text-white px-2.5 py-1 rounded-lg text-[11px] font-semibold shrink-0 active:scale-95 transition-transform">
        Resume
      </button>
      <button onClick={(e) => { e.stopPropagation(); onDelete(); }} className="text-slate-300 hover:text-red-400 text-sm shrink-0 transition-colors">
        &#x2715;
      </button>
    </div>
  );
}

function getTimeAgo(dateStr: string): string {
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}

// ── Animated Stat Card ─────────────────────────────────────────────────────────

function AnimatedStatCard({ label, value, total, suffix, color }: {
  label: string;
  value: number;
  total?: number;
  suffix?: string;
  color: 'teal' | 'indigo';
}) {
  const [displayValue, setDisplayValue] = useState(0);

  useEffect(() => {
    if (value === 0) { setDisplayValue(0); return; }
    const duration = 800;
    const start = performance.now();
    const animate = (now: number) => {
      const elapsed = now - start;
      const progress = Math.min(elapsed / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      setDisplayValue(Math.round(value * eased));
      if (progress < 1) requestAnimationFrame(animate);
    };
    requestAnimationFrame(animate);
  }, [value]);

  const gradients = {
    teal: 'from-teal-50 to-emerald-50 dark:from-teal-500/10 dark:to-emerald-500/10 border-teal-200',
    indigo: 'from-indigo-50 to-purple-50 dark:from-indigo-500/10 dark:to-purple-500/10 border-indigo-200',
  };
  const textColors = {
    teal: 'text-teal-700',
    indigo: 'text-indigo-700',
  };

  return (
    <div className={`bg-gradient-to-br ${gradients[color]} border rounded-2xl p-4 text-center`}>
      <div className={`text-3xl font-bold ${textColors[color]}`}>
        {displayValue}{suffix}
      </div>
      {total !== undefined && (
        <div className="text-xs text-slate-400 mt-0.5">of {total}</div>
      )}
      <div className="text-xs text-slate-600 font-medium mt-1">{label}</div>
    </div>
  );
}

// ── Section Stat Row ───────────────────────────────────────────────────────────

function MarkedForReviewCard({ allQuestions, createSession, navigate, sections, scienceSections, contextType }: {
  allQuestions: any[];
  createSession: ReturnType<typeof useCreateSession>;
  navigate: ReturnType<typeof useNavigate>;
  sections: any[];
  scienceSections?: any[];
  contextType: 'exam' | 'class';
}) {
  const flagged = getFlaggedQuestions();
  const questionIds = allQuestions.map(q => q.id);
  const flaggedInContext = flagged.filter(id => questionIds.includes(id));

  if (flaggedInContext.length === 0) return null;

  const handleStudyFlagged = async () => {
    const sectionId = contextType === 'exam'
      ? (scienceSections?.[0]?.id ?? sections[0]?.id)
      : sections[0]?.id;
    if (!sectionId || flaggedInContext.length === 0) return;

    const body: any = {
      questionIds: flaggedInContext,
      label: 'Marked for Review',
    };
    if (contextType === 'exam') {
      body.examSectionId = sectionId;
    } else {
      body.classSectionId = sectionId;
    }

    const result = await createSession.mutateAsync(body);
    const ctxType = contextType === 'exam' ? 'exam' : 'class';
    navigate(`/study/${ctxType}/${sectionId}?session=${result.session.id}`);
  };

  return (
    <button
      onClick={handleStudyFlagged}
      disabled={createSession.isPending}
      className="w-full bg-white border border-amber-200 rounded-2xl p-4 flex items-center gap-3 active:bg-amber-50 transition-colors disabled:opacity-50"
    >
      <div className="w-10 h-10 rounded-xl bg-amber-50 flex items-center justify-center shrink-0">
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5 text-amber-400">
          <path strokeLinecap="round" strokeLinejoin="round" d="M11.48 3.499a.562.562 0 011.04 0l2.125 5.111a.563.563 0 00.475.345l5.518.442c.499.04.701.663.321.988l-4.204 3.602a.563.563 0 00-.182.557l1.285 5.385a.562.562 0 01-.84.61l-4.725-2.885a.563.563 0 00-.586 0L6.982 20.54a.562.562 0 01-.84-.61l1.285-5.386a.562.562 0 00-.182-.557l-4.204-3.602a.563.563 0 01.321-.988l5.518-.442a.563.563 0 00.475-.345L11.48 3.5z" />
        </svg>
      </div>
      <div className="flex-1 text-left">
        <div className="text-sm font-semibold text-squishy-dark">Marked for Review</div>
        <div className="text-xs text-slate-500">{flaggedInContext.length} question{flaggedInContext.length !== 1 ? 's' : ''} flagged</div>
      </div>
      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-4 h-4 text-slate-400">
        <path fillRule="evenodd" d="M7.21 14.77a.75.75 0 01.02-1.06L11.168 10 7.23 6.29a.75.75 0 111.04-1.08l4.5 4.25a.75.75 0 010 1.08l-4.5 4.25a.75.75 0 01-1.06-.02z" clipRule="evenodd" />
      </svg>
    </button>
  );
}

function SectionStatRow({ stat, onStart }: { stat: ExamSectionStat; onStart: () => void }) {
  const progress = stat.totalQuestions > 0 ? Math.round((stat.attempted / stat.totalQuestions) * 100) : 0;

  return (
    <div className="bg-white border rounded-xl p-4 flex items-center gap-3">
      <div className="flex-1 min-w-0">
        <div className="flex items-baseline gap-2">
          <span className="font-semibold text-sm text-squishy-dark truncate">{stat.sectionName}</span>
          <span className="text-[10px] text-slate-400 uppercase">{stat.sectionCode}</span>
        </div>
        <div className="flex items-center gap-3 mt-1.5">
          <div className="flex-1 h-2 bg-slate-100 rounded-full overflow-hidden">
            <div
              className="h-full bg-gradient-to-r from-teal-400 to-emerald-500 rounded-full transition-all duration-700"
              style={{ width: `${progress}%` }}
            />
          </div>
          <span className="text-[10px] text-slate-500 shrink-0">{stat.attempted}/{stat.totalQuestions}</span>
        </div>
        <div className="text-[10px] text-slate-400 mt-1">
          {stat.accuracy}% accuracy
        </div>
      </div>
      <button
        onClick={onStart}
        className="bg-squishy-primary/10 text-squishy-primary px-3 py-2 rounded-lg text-xs font-semibold active:scale-95 transition-transform shrink-0"
      >
        Study
      </button>
    </div>
  );
}
